import { useEffect, useRef } from "react";
import { toast } from "sonner";

const VERSION_URL = "/version.json";
const STORAGE_KEY = "lastUpdatePromptAt";
const TWO_DAYS_MS = 48 * 60 * 60 * 1000;
const POLL_INTERVAL_MS = 60 * 60 * 1000; // 1h
const ADMIN_POLL_INTERVAL_MS = 60 * 1000; // 1min for admins
const SEEN_VERSION_KEY = "lastSeenAppVersion";

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data?.version ?? null;
  } catch {
    return null;
  }
}

function canPrompt(): boolean {
  try {
    const last = Number(localStorage.getItem(STORAGE_KEY) ?? 0);
    return !last || Date.now() - last > TWO_DAYS_MS;
  } catch {
    return true;
  }
}

function markPrompted() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function useVersionCheck(enabled: boolean = true, isAdmin: boolean = false) {
  const currentVersion = useRef<string | null>(null);
  const shownRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const showToast = () => {
      // Admins: always show on every new version (bypass cooldown & once-per-session gate).
      // Everyone else: throttle to avoid overwhelming (once per session + 2-day cooldown).
      if (!isAdmin) {
        if (shownRef.current || !canPrompt()) return;
        shownRef.current = true;
        markPrompted();
      }
      toast("Fresh updates just landed! 🌸", {
        id: "app-version-refresh",
        description:
          "We've been tidying things up behind the scenes. Refresh to get the latest goodies.",
        duration: Infinity,
        action: {
          label: "Refresh",
          onClick: () => window.location.reload(),
        },
        actionButtonStyle: {
          background: "hsl(var(--going))",
          color: "hsl(var(--going-foreground))",
          border: "1px solid hsl(var(--going-border))",
        },
      });
    };

    const check = async () => {
      const v = await fetchVersion();
      if (cancelled || !v) return;

      if (currentVersion.current === null) {
        currentVersion.current = v;
        if (isAdmin) {
          // Admins: also catch versions published while the app was closed
          let seen: string | null = null;
          try {
            seen = localStorage.getItem(SEEN_VERSION_KEY);
          } catch {
            // ignore
          }
          if (seen && seen !== v) showToast();
          try {
            localStorage.setItem(SEEN_VERSION_KEY, v);
          } catch {
            // ignore
          }
        }
        return;
      }

      if (v !== currentVersion.current) {
        currentVersion.current = v;
        if (isAdmin) {
          try {
            localStorage.setItem(SEEN_VERSION_KEY, v);
          } catch {
            // ignore
          }
        }
        showToast();
      }
    };

    check();

    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = window.setInterval(
      check,
      isAdmin ? ADMIN_POLL_INTERVAL_MS : POLL_INTERVAL_MS
    );

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [enabled, isAdmin]);
}
